import {
  Add as AddIcon
} from '@mui/icons-material';

import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Box, Button, Paper, TextField, Typography, CircularProgress, Fade } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import EditKanbanColumnSettingsModal from '../../components/EditKanbanColumnSettingsModal';
import KanbanColumnNew from "../../components/KanbanNew/KanbanColumnNew";
import KanbanHeader from "../../components/KanbanNew/KanbanHeader";
import CreateOrEditKanbanSettingsModal from '../../components/CreateOrEditKanbanSettingsModal';
import CreateOrEditKanbanPublicLinkModal from '../../components/CreateOrEditKanbanPublicLinkModal';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useEvent } from '../../hooks/useEvent';
import socket from '../../hooks/useSocket';
import api from '../../services/api';
import kanbanDataService from '../../services/kanbanDataService';
import { handleCardActions, handleColumnActions } from './helpers';
import countFilterDifferences from '../../helpers/countFilterDifferences';

const USER_COLORS = [
  '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
  '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
  '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'
];

const generateRandomColor = (userId) => {
  const colorIndex = typeof userId === 'string'
    ? userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % USER_COLORS.length
    : userId % USER_COLORS.length;
  return USER_COLORS[colorIndex];
};

const initialFilterFormated = {
  priority: [],
  tag: [],
  assigned: [],
  requester: null,
  date: {
    type: null,
    range: { startDate: null, endDate: null, key: "selection" }
  },
  searchText: "",
  createdBy: []
};

const initialFilter = {
  priority: [],
  tag: [],
  assigned: [],
  requester: []
};

const initialLoading = {
  board: false,
  priority: false,
  users: false,
  listBoard: false,
  tag: false
};

const DISPLAY_SETTINGS = {
  showTaskCount: true,
  showTags: true,
  showPriorityName: true,
  showTicketNumber: false,
  showCardAuthorship: true
};

function KanbanBoardMUI() {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const emitter = useEvent();
  const { kanbanId } = useParams();
  const [kanbanFormated, setKanbanFormated] = useState(null);

  const [displayFilters, setDisplayFilters] = useState(() => {
    const savedDisplayFilters = localStorage.getItem(`kanban_display_filters_${kanbanId}`);
    return savedDisplayFilters ? JSON.parse(savedDisplayFilters) : DISPLAY_SETTINGS;
  });

  const [openSettings, setOpenSettings] = useState(false);
  const [openPublicLink, setOpenPublicLink] = useState(false);
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [addingColumnLoading, setAddingColumnLoading] = useState(false);

  const [loading, setLoading] = useState(initialLoading);

  const [priorities, setPriorities] = useState([]);
  const [kanbanList, setKanbanList] = useState([]);
  const [cardTags, setCardTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  // Restaurar filtros do cache
  const cacheKey = `kanban_filters_${kanbanId}`;
  const savedFilters = useRef(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const cached = useRef(savedFilters.current());

  const [createdByFilter, setCreatedByFilter] = useState(() => cached.current?.createdBy || []);
  const [requester, setRequester] = useState(() => cached.current?.requester || null);

  const [expanded, setExpanded] = useState(() => cached.current ? true : false);

  const [loadingCard, setLoadingCard] = useState({});
  const [searchText, setSearchText] = useState(() => cached.current?.searchText || '');
  const [filters, setFilters] = useState(() => cached.current?.filters || initialFilter);

  const [dateFilter, setDateFilter] = useState(() => {
    const df = cached.current?.dateFilter;
    if (df?.type) {
      return {
        ...df,
        range: {
          ...df.range,
          startDate: df.range.startDate ? new Date(df.range.startDate) : null,
          endDate: df.range.endDate ? new Date(df.range.endDate) : null,
        }
      };
    }
    return { type: null, range: { startDate: null, endDate: null, key: 'selection' } };
  });

  const [settingsOpenIn, setSettingsOpenIn] = useState('display');
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState(null);

  const isMountedRef = useRef(true);
  const prevKanbanIdRef = useRef(kanbanId);

  // Ao trocar de board, restaurar filtros do novo kanbanId (ou resetar)
  useEffect(() => {
    if (prevKanbanIdRef.current === kanbanId) return;
    prevKanbanIdRef.current = kanbanId;

    let newCached = null;
    try {
      const raw = localStorage.getItem(`kanban_filters_${kanbanId}`);
      newCached = raw ? JSON.parse(raw) : null;
    } catch { /* ignore */ }

    setFilters(newCached?.filters || initialFilter);
    setCreatedByFilter(newCached?.createdBy || []);
    setRequester(newCached?.requester || null);
    setSearchText(newCached?.searchText || '');

    const df = newCached?.dateFilter;
    if (df?.type) {
      setDateFilter({
        ...df,
        range: {
          ...df.range,
          startDate: df.range.startDate ? new Date(df.range.startDate) : null,
          endDate: df.range.endDate ? new Date(df.range.endDate) : null,
        }
      });
    } else {
      setDateFilter({ type: null, range: { startDate: null, endDate: null, key: 'selection' } });
    }

    setExpanded(!!newCached);

    const savedDisplay = localStorage.getItem(`kanban_display_filters_${kanbanId}`);
    setDisplayFilters(savedDisplay ? JSON.parse(savedDisplay) : DISPLAY_SETTINGS);
  }, [kanbanId]);

  const filterObject = {
    ...filters,
    date: dateFilter,
    searchText,
    createdBy: createdByFilter,
    requester
  };

  const totalChanges = countFilterDifferences(initialFilterFormated, filterObject);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onDisplayChange = filters => {
    setDisplayFilters(filters)
  }

  const fetchBoardData = async () => {
    if (!kanbanId) return;

    // PRIORIDADES
    (async () => {
      setLoading(prev => ({ ...prev, priority: true }));
      try {
        const res = await api.get(`/kanban/priority/${kanbanId}`);
        if (isMountedRef.current) setPriorities(res.data);
      } catch {
        if (isMountedRef.current) setPriorities([]);
      } finally {
        if (isMountedRef.current) setLoading(prev => ({ ...prev, priority: false }));
      }
    })();

    // TAGS
    (async () => {
      setLoading(prev => ({ ...prev, tag: true }));
      try {
        const res = await api.get(`/kanban/tag/${kanbanId}`);
        if (isMountedRef.current) setCardTags(res.data);
      } catch {
        if (isMountedRef.current) setCardTags([]);
      } finally {
        if (isMountedRef.current) setLoading(prev => ({ ...prev, tag: false }));
      }
    })();

    // LISTA DE BOARDS
    (async () => {
      setLoading(prev => ({ ...prev, listBoard: true }));
      try {
        const res = await api.get("/kanbans/list");
        if (isMountedRef.current) {
          setKanbanList(res.data.map(board => ({
            id: board.id,
            title: board.title,
            description: board.description,
            starred: board.starred || false
          })));
        }
      } catch {
        if (isMountedRef.current) setKanbanList([]);
      } finally {
        if (isMountedRef.current) setLoading(prev => ({ ...prev, listBoard: false }));
      }
    })();

    // USERS
    (async () => {
      setLoading(prev => ({ ...prev, users: true }));
      try {
        const res = await api.get("/users");
        if (isMountedRef.current) {
          setUsers(res.data.map(user => ({
            id: user.id,
            name: user.name,
            avatar: user.avatar,
            color: generateRandomColor(user.id)
          })));
        }
      } catch {
        if (isMountedRef.current) setUsers([]);
      } finally {
        if (isMountedRef.current) setLoading(prev => ({ ...prev, users: false }));
      }
    })();
  };

  useEffect(() => {
    if (!kanbanId) return;
    fetchBoardData();
  }, [kanbanId]);

  const fetchBoard = useCallback(async () => {
    if (!kanbanId) return;
    setLoading(prev => ({ ...prev, board: true }));
    try {
      const res = await api.get(`/kanban/board-new/${kanbanId}`, { params: filterObject });
      if (!isMountedRef.current) return;

      const transformedData = await kanbanDataService.transformKanbanData(res.data);
      setKanbanFormated(transformedData);

      setCurrentBoard({
        id: res.data.kanban.id,
        title: res.data.kanban.title,
        taskTitle: res.data.kanban.taskTitle,
        starred: res.data.kanban.starred || false,
        assigneds: res.data.kanban.assigneds || [],
        publicEnabled: !!res.data.kanban.publicEnabled,
      });
    } catch (err) {
      console.error("Erro ao carregar board:", err);
    } finally {
      if (isMountedRef.current) setLoading(prev => ({ ...prev, board: false }));
    }
  }, [kanbanId, filters, dateFilter, searchText, createdByFilter, requester]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const handleLoadMore = useCallback(async (columnId) => {
    // Existing code remains the same
    if (!kanbanFormated || !kanbanFormated.columns[columnId]) return;

    let { hasMore, page } = kanbanFormated.columns[columnId].pagination;
    if (!hasMore) return;

    try {
      setLoadingCard(prev => ({
        ...prev,
        [columnId]: true,
        ...(kanbanFormated.columns[columnId].cardIds || []).reduce((acc, cardId) => {
          acc[cardId] = true;
          return acc;
        }, {})
      }));

      const nextPage = page + 1;
      const { data } = await api.get(`/kanban/column/list/${columnId.replace('column-', '')}`, {
        params: { pageNumber: nextPage, limit: 15, filters: filterObject }
      });

      if (!isMountedRef.current) return;

      const updatedKanban = kanbanDataService.mergeNewCards(
        kanbanFormated,
        columnId,
        { ...data, page: nextPage }
      );

      setKanbanFormated(updatedKanban);

      const finalLoadingCard = {
        ...loadingCard,
        [columnId]: false,
        ...(updatedKanban.columns[columnId].cardIds || []).reduce((acc, cardId) => {
          acc[cardId] = false;
          return acc;
        }, {})
      };

      setLoadingCard(finalLoadingCard);

    } catch (err) {
      console.error("Erro ao buscar cards:", err);

      if (isMountedRef.current) {
        setLoadingCard(prev => ({
          ...prev,
          [columnId]: false,
          ...(kanbanFormated.columns[columnId].cardIds || []).reduce((acc, cardId) => {
            acc[cardId] = false;
            return acc;
          }, {})
        }));
      }
    }
  }, [kanbanFormated, loadingCard]);

  const onDragEnd = useCallback(async (result) => {
    // Existing code remains the same
    const { draggableId, source, destination, type } = result;

    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    try {
      setLoadingCard(prev => ({ ...prev, [draggableId]: true }));

      if (type === 'column') {
        setKanbanFormated(prevKanban => {
          const newColumnOrder = [...prevKanban.columnOrder];
          newColumnOrder.splice(source.index, 1);
          newColumnOrder.splice(destination.index, 0, draggableId);

          return {
            ...prevKanban,
            columnOrder: newColumnOrder
          };
        });

        const columnId = draggableId.replace('column-', '');
        await api.put(`/kanban/column/${columnId}`, {
          position: destination.index
        });
      } else {
        const newState = await new Promise(resolve => {
          setKanbanFormated(prevKanban => {
            const newKanban = { ...prevKanban };
            const newColumns = { ...newKanban.columns };

            const sourceColumn = { ...newColumns[source.droppableId] };
            const destColumn = source.droppableId === destination.droppableId
              ? sourceColumn
              : { ...newColumns[destination.droppableId] };

            const sourceCardIds = [...sourceColumn.cardIds];
            const destCardIds = source.droppableId === destination.droppableId
              ? sourceCardIds
              : [...destColumn.cardIds];

            sourceCardIds.splice(source.index, 1);
            destCardIds.splice(destination.index, 0, draggableId);

            sourceColumn.cardIds = sourceCardIds;

            if (source.droppableId !== destination.droppableId) {
              destColumn.cardIds = destCardIds;

              const cardToMove = { ...sourceColumn.cards[draggableId] };

              destColumn.cards = {
                ...destColumn.cards,
                [draggableId]: cardToMove
              };

              delete sourceColumn.cards[draggableId];

              if (sourceColumn.pagination && destColumn.pagination) {
                sourceColumn.pagination = {
                  ...sourceColumn.pagination,
                  count: Math.max(0, Number(sourceColumn.pagination.count || 0) - 1)
                };

                destColumn.pagination = {
                  ...destColumn.pagination,
                  count: Number(destColumn.pagination.count || 0) + 1
                };
              }
            }

            newColumns[source.droppableId] = sourceColumn;
            if (source.droppableId !== destination.droppableId) {
              newColumns[destination.droppableId] = destColumn;
            }

            const result = {
              ...newKanban,
              columns: newColumns
            };

            resolve(result);
            return result;
          });
        });

        const cardId = draggableId.replace('card-', '');
        const destColumnId = destination.droppableId.replace('column-', '');

        const updatedCard = await api.put(`/kanban/card/${cardId}`, {
          order: destination.index,
          columnId: destColumnId
        });
      }
    } catch (error) {
      console.error("Erro durante a operação de arrastar e soltar:", error);
      if (isMountedRef.current) {
        fetchBoardData();
        fetchBoard();
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingCard(prev => ({ ...prev, [draggableId]: false }));
      }
    }
  }, [kanbanId, fetchBoardData, fetchBoard]);

  const handleAddItem = useCallback(async (columnId, item) => {
    const { title } = item;
    if (!title.trim()) return;

    try {
      setLoadingCard(prev => ({ ...prev, [columnId]: true }));

      const { data: newCard } = await api.post(`/kanban/card/${columnId.replace('column-', '')}`, { title });

      if (!isMountedRef.current) return;

      setKanbanFormated(prevKanban => {
        if (!prevKanban) return prevKanban;

        const newKanban = { ...prevKanban };
        const column = { ...newKanban.columns[columnId] };

        const formattedCardId = `card-${newCard.id}`;
        const formattedCard = {
          id: formattedCardId,
          name: newCard.title || '',
          description: newCard?.description || '',
          tags: newCard?.tags,
          amount: newCard?.amount,
          order: newCard?.order,
          creator: newCard?.creator,
          createdAt: newCard?.createdAt,
          updatedAt: newCard?.updatedAt,
          kanbanPriorityLevel: newCard?.kanbanPriorityLevel,
          taskNumber: newCard?.taskNumber,
          assigned: newCard?.assigned,
          requester: newCard.requester,
          tasks: newCard?.tasks
        };

        column.cardIds = [formattedCardId, ...column.cardIds];

        if (!column.cards) column.cards = {};
        column.cards[formattedCardId] = formattedCard;

        if (!newKanban.cards) newKanban.cards = {};

        newKanban.cards[formattedCardId] = formattedCard;

        if (column.pagination) {
          column.pagination = {
            ...column.pagination,
            count: (column.pagination.count || 0) + 1
          };
        }

        // Atualiza a coluna no kanban
        newKanban.columns[columnId] = column;

        return newKanban;
      });

    } catch (error) {
      console.error("Erro ao adicionar card:", error);
    } finally {
      if (isMountedRef.current) {
        setLoadingCard(prev => ({ ...prev, [columnId]: false }));
      }
    }
  }, [kanbanId, isMountedRef]);

  const handleAddColumn = useCallback(async () => {
    if (!newColumnTitle.trim()) return;

    try {
      setAddingColumnLoading(true); // Add loading state for the button
      setLoading(prev => ({ ...prev, board: true }));

      const { data: newColumn } = await api.post(`/kanban/column/${kanbanId}`, {
        title: newColumnTitle
      });

      if (!isMountedRef.current) return;

      setKanbanFormated(prevKanban => {
        if (!prevKanban) return prevKanban;

        const newKanban = { ...prevKanban };
        const columnId = `column-${newColumn.id}`;

        newKanban.columns = {
          ...newKanban.columns,
          [columnId]: {
            id: columnId,
            title: newColumn.title || '',
            position: newColumn.position,
            cardIds: [],
            cards: {},
            pagination: {
              page: 1,
              count: 0,
              hasMore: false
            }
          }
        };

        newKanban.columnOrder = [...newKanban.columnOrder, columnId];

        return newKanban;
      });

      setIsAddingColumn(false);
      setNewColumnTitle('');

    } catch (error) {
      console.error("Erro ao adicionar coluna:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(prev => ({ ...prev, board: false }));
        setAddingColumnLoading(false); // Reset loading state
      }
    }
  }, [kanbanId, newColumnTitle, isMountedRef]);

  const handleCreateTag = useCallback(() => {
    setSettingsOpenIn('tags');
    setOpenSettings(true);
  }, []);

  const handleCreatePriority = useCallback(() => {
    setSettingsOpenIn('priorities');
    setOpenSettings(true);
  }, []);

  const handleBoardChange = useCallback((selectedBoard) => {
    if (selectedBoard && selectedBoard.id !== kanbanId) {
      history.push(`/kanban/b/${selectedBoard.id}`);
    }
  }, [history, kanbanId]);

  useEffect(() => {
    if (!kanbanId) return;

    const onColumn = (data) => handleColumnActions(data, setKanbanFormated, kanbanId);
    const onCard = (data) => handleCardActions(data, setKanbanFormated, kanbanId);

    const refetchCardAndDispatch = async (cardId) => {
      if (!cardId) return;
      const rawId = String(cardId).replace(/^card-/, '');
      try {
        const { data } = await api.get(`/kanban/card/${rawId}`);
        if (!data) return;
        if (String(data.kanbanId) !== String(kanbanId)) return;
        emitter.emit('kanban-card', {
          action: 'update',
          kanbanId: data.kanbanId,
          cardId: data.id,
          columnId: data.kanbanColumnId,
          card: data,
        });
      } catch (err) {
        console.error('Erro ao recarregar card:', err);
      }
    };

    const onAssigneds = (data) => {
      if (!data?.cardId) return;
      refetchCardAndDispatch(data.cardId);
    };
    const onTask = (data) => {
      const cardId = data?.cardId || data?.task?.kanbanCardId;
      if (cardId) refetchCardAndDispatch(cardId);
    };
    const onTaskChecklist = (data) => {
      const cardId =
        data?.cardId ||
        data?.checklist?.kanbanCardId ||
        data?.task?.kanbanCardId;
      if (cardId) refetchCardAndDispatch(cardId);
    };
    const onComments = (data) => {
      const cardId = data?.kanbanCardId || data?.comment?.kanbanCardId || data?.cardId;
      if (cardId) refetchCardAndDispatch(cardId);
    };

    emitter.on('kanban-column', onColumn);
    emitter.on('kanban-card', onCard);
    emitter.on('kanban-assigneds', onAssigneds);
    emitter.on('kanban-task', onTask);
    emitter.on('kanban-task-checklist', onTaskChecklist);
    emitter.on('kanban-comments', onComments);

    socket.on('kanban-column', onColumn);
    socket.on('kanban-card', onCard);
    socket.on('kanban-assigneds', onAssigneds);
    socket.on('kanban-task', onTask);
    socket.on('kanban-task-checklist', onTaskChecklist);
    socket.on('kanban-comments', onComments);

    return () => {
      emitter.off('kanban-column', onColumn);
      emitter.off('kanban-card', onCard);
      emitter.off('kanban-assigneds', onAssigneds);
      emitter.off('kanban-task', onTask);
      emitter.off('kanban-task-checklist', onTaskChecklist);
      emitter.off('kanban-comments', onComments);

      // Antes faltavam estes 6 socket.off — cada vez que kanbanId mudava,
      // 6 listeners novos eram adicionados sem que os antigos saíssem.
      socket.off('kanban-column', onColumn);
      socket.off('kanban-card', onCard);
      socket.off('kanban-assigneds', onAssigneds);
      socket.off('kanban-task', onTask);
      socket.off('kanban-task-checklist', onTaskChecklist);
      socket.off('kanban-comments', onComments);
    };
  }, [kanbanId, emitter]);



  useEffect(() => {
    if (kanbanId) {
      localStorage.setItem(`kanban_display_filters_${kanbanId}`, JSON.stringify(displayFilters));
    }
  }, [displayFilters, kanbanId]);

  // Salvar filtros no cache
  useEffect(() => {
    if (!kanbanId) return;
    const hasFilters = filters.tag.length > 0 || filters.priority.length > 0 || filters.assigned.length > 0
      || createdByFilter.length > 0 || requester || dateFilter.type || searchText;

    if (hasFilters) {
      localStorage.setItem(cacheKey, JSON.stringify({
        filters,
        createdBy: createdByFilter,
        requester,
        dateFilter,
        searchText,
      }));
    } else {
      localStorage.removeItem(cacheKey);
    }
  }, [kanbanId, filters, createdByFilter, requester, dateFilter, searchText, cacheKey]);

  const BoardLoadingOverlay = () => (
    <Fade in={loading.board} timeout={200}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme) => alpha(
            theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
            0.75
          ),
          backdropFilter: 'blur(3px)',
          zIndex: 1200,
          pointerEvents: loading.board ? 'auto' : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? alpha('#1e1e1e', 0.9) : alpha('#ffffff', 0.9),
            borderRadius: 2,
            py: 3,
            px: 4,
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.2)'
                : '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              mb: 2,
              color: (theme) => theme.palette.primary.main,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: (theme) => theme.palette.text.primary,
              mb: 0.5,
              textAlign: 'center',
            }}
          >
            Carregando Quadro
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textAlign: 'center',
            }}
          >
            Preparando seu quadro de tarefas...
          </Typography>
        </Box>
      </Box>
    </Fade>
  );

  return (
    <>
      <KanbanHeader
        loading={loading}
        users={users}
        boardsList={kanbanList}
        currentBoard={currentBoard}
        onBoardChange={handleBoardChange}
        handleCreateTag={handleCreateTag}
        handleCreatePriority={handleCreatePriority}

        tags={cardTags}
        selectedTags={filters.tag}
        setSelectedTags={value => setFilters(prevFilters => ({ ...prevFilters, tag: value }))}

        priorities={priorities}
        priorityFilter={filters.priority}
        setPriorityFilter={value => setFilters(prevFilters => ({ ...prevFilters, priority: value }))}

        assigneds={currentBoard?.assigneds}
        assignedFilter={filters.assigned}
        setAssignedFilter={value => setFilters(prevFilters => ({ ...prevFilters, assigned: value }))}

        requester={requester}
        setRequester={setRequester}

        createdByFilter={createdByFilter}
        setCreatedByFilter={setCreatedByFilter}

        searchText={searchText}
        setSearchText={setSearchText}

        expanded={expanded}
        setExpanded={setExpanded}

        dateFilter={dateFilter}
        setDateFilter={setDateFilter}

        openSettings={openSettings}
        setOpenSettings={setOpenSettings}
        onOpenPublicLink={() => setOpenPublicLink(true)}
        taskTitle={kanbanFormated?.taskTitle || 'Protocolo'}

        count={totalChanges}
      />
      <Box
        component="div"
        sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#161c24' : '#ffffff',
          position: 'relative',
        }}
      >
        {/* Simple centered loading overlay */}
        <BoardLoadingOverlay />

        <CreateOrEditKanbanPublicLinkModal
          open={openPublicLink}
          onClose={() => setOpenPublicLink(false)}
          kanbanId={kanbanId}
          initialPublicEnabled={!!currentBoard?.publicEnabled}
        />
        <CreateOrEditKanbanSettingsModal
          openIn={settingsOpenIn}
          open={openSettings}
          onClose={() => setOpenSettings(false)}
          tags={cardTags}
          priorities={priorities}
          assigneds={currentBoard?.assigneds}
          kanbanId={kanbanId}
          displayFilters={displayFilters}
          setDisplayFilters={setDisplayFilters}
          onDisplayChange={onDisplayChange}
        />
        <EditKanbanColumnSettingsModal
          kanban={kanbanFormated}
          columnId={selectedColumnId}
          open={openSettingsModal}
          onClose={setOpenSettingsModal}
        />
        <DragDropContext onDragEnd={onDragEnd}>
          <Box
            sx={{
              marginTop: expanded ? '175px' : '65px',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2,
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                }
              },
            }}
          >
            <Droppable
              droppableId="all-droppables"
              direction="horizontal"
              type="column"
            >
              {provided => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {(kanbanFormated?.columnOrder || []).map((columnId, index) => (
                    <KanbanColumnNew
                      key={columnId}
                      kanbanId={kanbanId}
                      column={kanbanFormated.columns[columnId]}
                      index={index}
                      onAddItem={handleAddItem}
                      onLoadMore={() => handleLoadMore(columnId)}
                      loadingCard={loadingCard}
                      expanded={expanded}
                      tags={cardTags}
                      priorities={priorities}
                      assigneds={currentBoard?.assigneds}
                      displayFilters={displayFilters}
                      taskTitle={kanbanFormated?.taskTitle || 'Protocolo'}
                      handleOpenSettingsModal={columnId => {
                        setOpenSettingsModal(true);
                        setSelectedColumnId(columnId);
                      }}
                    />
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
            <Paper
              elevation={0}
              sx={{
                minWidth: 280,
                maxWidth: 280,
                mt: '5px',
                ml: 2,
                borderRadius: 2,
                backgroundColor: isAddingColumn
                  ? (theme) => theme.palette.mode === 'dark' ? '#1e2732' : '#ffffff'
                  : (theme) => alpha(theme.palette.mode === 'dark' ? '#1e2732' : '#ffffff', 0.8),
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                height: isAddingColumn ? 'auto' : 44,
              }}
            >
              {isAddingColumn ? (
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    animation: 'fadeIn 0.3s ease-in-out',
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0, transform: 'translateY(5px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 0.5
                    }}
                  >
                    Nova Coluna
                  </Typography>

                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Digite o título da lista..."
                    value={newColumnTitle}
                    onChange={(e) => setNewColumnTitle(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newColumnTitle.trim()) {
                        handleAddColumn();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.05)
                            : alpha(theme.palette.common.black, 0.03),
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: (theme) => theme.palette.primary.main
                          }
                        },
                        '&.Mui-focused': {
                          boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '10px 14px',
                        fontSize: '0.9rem'
                      }
                    }}
                  />

                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between'
                  }}>
                    <Button
                      variant="contained"
                      onClick={handleAddColumn}
                      disabled={!newColumnTitle.trim() || addingColumnLoading}
                      sx={{
                        py: 0.8,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        boxShadow: (theme) => `0 2px 4px ${alpha(theme.palette.primary.main, 0.25)}`,
                        transition: 'all 0.2s ease',
                        backgroundColor: (theme) => theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                          boxShadow: (theme) => `0 4px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                          transform: 'translateY(-1px)'
                        },
                        flex: 1
                      }}
                    >
                      {addingColumnLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress
                            size={16}
                            thickness={5}
                            sx={{
                              color: 'white',
                              animationDuration: '0.65s'
                            }}
                          />
                          <span>Adicionando...</span>
                        </Box>
                      ) : 'Adicionar'}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsAddingColumn(false);
                        setNewColumnTitle('');
                      }}
                      disabled={addingColumnLoading}
                      sx={{
                        py: 0.8,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.2),
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05),
                          borderColor: (theme) => alpha(theme.palette.text.primary, 0.3),
                        },
                        flex: 1
                      }}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddingColumn(true)}
                  sx={{
                    height: '100%',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    px: 2,
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  Adicionar nova Coluna
                </Button>
              )}
            </Paper>
          </Box>
        </DragDropContext>
      </Box>
    </>
  );
}

export default React.memo(KanbanBoardMUI);